// TouchPoint types (mirrors backend DTOs for Email, Phone, Social, SMS, Meeting campaigns)

export enum TouchPointType {
  Email = 'Email',
  Phone = 'Phone',
  Social = 'Social',
  Meeting = 'Meeting',
}

// EmailSendRule — structured timing config for email touchpoints.
// Either an absolute send time (Kind=Scheduled) or an event-anchored offset (Kind=Event).

export enum EmailSendRuleKind {
  Scheduled = 'Scheduled',
  Event = 'Event',
}

export enum EmailOffsetUnit {
  Minute = 'Minute',
  Hour = 'Hour',
  Day = 'Day',
}

// Consent class for an email touchpoint (ORB-340). Marketing emails honor recipient
// opt-outs; Transactional emails always send. `null` lets the server smart-default.
export enum EmailComplianceClass {
  Marketing = 'Marketing',
  Transactional = 'Transactional',
}

// Per-touchpoint engagement stage a Download nurture targets (ORB-359). Wire values match the API's
// EnumExtensions.ToDescription() output. None = no engagement gate (the default for non-nurture emails).
export enum EmailEngagementTarget {
  None = 'None',
  Unopened = 'Unopened',
  OpenedNotClicked = 'OpenedNotClicked',
  ClickedNotConverted = 'ClickedNotConverted',
}

// How a Scheduled rule's local wall-clock time maps to UTC (ORB-352).
// FixedUtc: the local time/zone pair resolves to one absolute instant for all recipients.
// RecipientLocal: per-recipient local delivery — UI is a gated fast-follow; included here for type parity.
export enum EmailScheduledSendMode {
  FixedUtc = 'FixedUtc',
  RecipientLocal = 'RecipientLocal',
}

// Wire values match the API's EnumExtensions.ToDescription() output
// (the codebase-wide convention for enum serialization).
export enum EmailEventType {
  WebRegistered = 'Web Registered',
  OnlineEventStart = 'Online Event Start',
  OnlineEventEnd = 'Online Event End',
  AssetDownloaded = 'Asset Downloaded',
  MeetingBooked = 'Meeting Booked',
  DemoBooked = 'Demo Booked',
  // Post-event nurture anchors (server EmailEventType 6/7). Past-tense, positive offsets only.
  OnlineEventAttended = 'Online Event Attended',
  OnlineEventMissed = 'Online Event Missed',
  // ORB-536. The registrant-scoped pre-event reminder (server EmailEventType 8). Fires on the SAME
  // instant as OnlineEventStart but reaches the inverse audience — only the people who registered,
  // rather than the imported list minus them — so its CTA is "join" and its default class is the
  // opposite. Future-dated like the other event anchors, so negative offsets are allowed.
  OnlineEventStartRegistrants = 'Online Event Start Registrants',
  // ORB-508. The recipient clicked through from one of the web's marketing emails and their browser
  // reached the landing page. Past-tense, positive offsets only. On a Download web this is the only
  // anchor that can express "clicked but has not converted" — Web Registered and Asset Downloaded are
  // the same instant there, so a nudge on either reaches somebody who already has the asset.
  EmailClicked = 'Email Clicked',
}

// Anchors that fire for ONE recipient at the moment the event happened to them (server:
// IEmailRuleCatalogService.IsRecipientAnchored). Mirrored here because two editor affordances depend
// on it, and the server rejects the combinations rather than silently ignoring them (ORB-508):
// the engagement gate is applied only while building an AUDIENCE, so it means nothing on these.
//
// ⚠ OnlineEventStartRegistrants stays OUT despite its registrant-only audience: the worker sweeps
// that audience as a list and writes many rows (server: Path B), so the gate does apply. The server
// agrees — IsRecipientAnchored returns false for it.
const RECIPIENT_ANCHORED_EVENT_TYPES: ReadonlySet<EmailEventType> = new Set([
  EmailEventType.WebRegistered,
  EmailEventType.AssetDownloaded,
  EmailEventType.MeetingBooked,
  EmailEventType.DemoBooked,
  EmailEventType.EmailClicked,
])

/** True when the rule sends to one person at its trigger instant rather than to a list. */
export function isRecipientAnchoredRule(rule: EmailSendRuleDto | null | undefined): boolean {
  if (!rule || rule.Kind !== EmailSendRuleKind.Event || !rule.EventType) return false
  return RECIPIENT_ANCHORED_EVENT_TYPES.has(rule.EventType)
}

export interface EmailSendRuleDto {
  Kind: EmailSendRuleKind
  SendAtAbsolute?: string | null    // ISO 8601 DateTime; populated only when Kind=Scheduled.
                                    // Server-computed from the local-time/zone pair below — the editor leaves this null.
  EventType?: EmailEventType | null // populated only when Kind=Event
  OffsetValue?: number | null       // signed; e.g. -5 = "5 days before anchor"
  OffsetUnit?: EmailOffsetUnit | null
  // Tz-aware scheduling (ORB-352) — populated only when Kind=Scheduled.
  ScheduledSendLocalTime?: string | null   // ISO local wall-clock (no offset), e.g. "2026-06-15T09:00:00"
  ScheduledSendTimeZone?: string | null    // IANA zone, e.g. "America/New_York"
  ScheduledSendMode?: EmailScheduledSendMode | null // default FixedUtc (server). RecipientLocal UI is a gated fast-follow.
}

// Which metric decides the winning subject in an A/B subject split test (ORB-453). Default Opens.
export enum SplitTestWinMetric {
  Opens = 'Opens',
  Clicks = 'Clicks',
}

// A/B subject-line split-test config on a scheduled email touchpoint (ORB-453). Inert unless Enabled,
// and only offered when the copy has two subjects. TestCohortPercent is 1–50; MeasurementWindowMinutes
// is required (the UI collects whole hours and sends hours × 60; server floor 60 min); WinMetric defaults to Opens.
export interface SubjectSplitTestDto {
  Enabled: boolean
  TestCohortPercent: number
  MeasurementWindowMinutes: number
  WinMetric?: SplitTestWinMetric | null
}

// Email-specific properties — touchpoint is a reference to an EmailCopy asset
// plus a structured EmailSendRule that decides when it fires.
export interface EmailTouchPointPropertiesDto {
  EmailAssetId: string
  Rule: EmailSendRuleDto
  // Verified sending domain this email sends from. Required at save time (enforced server-side);
  // selected in the activation editor.
  FromSendingDomainId?: string | null
  // Consent class (ORB-340). Optional — server smart-defaults when null.
  ComplianceClass?: EmailComplianceClass | null
  // Engagement stage this Download nurture targets (ORB-359). Optional — null/None = no engagement gate.
  // The rule editor pre-fills the per-email-type default from the EmailRules lookup.
  EngagementTarget?: EmailEngagementTarget | null
  // Reply-To address override (ORB-340). Optional.
  ReplyToEmail?: string | null
  // Subset of the parent Web's included file imports this email targets (ORB-347).
  // null/empty = all of the Web's included file imports.
  TargetFileImportIds?: string[] | null
  // A/B subject-line split test (ORB-453). Omitted/null = no test. Only honored for scheduled sends
  // whose copy has two subjects.
  SubjectSplitTest?: SubjectSplitTestDto | null

  // Read-only: whether the activation is armed to send (ORB-378). Server-controlled — saving arms it,
  // Start-over disarms it, Reschedule re-arms it. A value sent on create/update is ignored.
  Armed?: boolean
}

// Meeting-specific properties
export interface MeetingTouchPointPropertiesDto {
  Type: string // Meeting type (e.g., "Planning call", "Foundations call")
  TimeZone: string // IANA timezone ID (e.g., "America/New_York")
  StartTime: string // ISO 8601 DateTime
  EndTime: string // ISO 8601 DateTime
  Attachment?: string
  MeetingUrlLink?: string

  // Webster Notetaker (recall.ai). SendWebster is settable on create/update (default true, an
  // opt-out). The rest are server-managed and read-only on the view; sending them on input is ignored.
  SendWebster?: boolean
  BotStatus?: string // WebsterBotStatus description: "Not Scheduled" | "Scheduled" | "In Call" | "Processing" | "Done" | "Failed" | "Unsupported URL"
  BotStatusSubCode?: string | null // recall.ai failure reason, present only when BotStatus is "Failed"
  HasRecording?: boolean // true when a playable MP4 is stored; fetch a fresh URL from the Recording endpoint on demand
  TranscriptJsonPath?: string // short-lived SAS URL to the transcript JSON, present once processing completes
}

// Short-lived SAS URL for playing back a Webster meeting recording
// (GET Workspaces/{workspaceId}/TouchPoints/{id}/Recording). Fetched on demand — the URL expires quickly.
export interface MeetingRecordingUrlDto {
  Url: string
  ExpiresInSeconds: number
}

// Short-lived SAS URL for fetching a Webster meeting's transcript JSON
// (GET Workspaces/{workspaceId}/TouchPoints/{id}/Transcript). Minted fresh on demand so a
// deep-linked/reloaded meeting page never depends on the stale SAS carried in the list payload.
export interface MeetingTranscriptUrlDto {
  Url: string
  ExpiresInSeconds: number
}

// Phone-specific properties
export interface PhoneTouchPointPropertiesDto {
  ScheduledDate: string // ISO 8601 DateTime
  NumberOfDials: number
}

// Social-specific properties
export interface SocialTouchPointPropertiesDto {
  ScheduledDate: string // ISO 8601 DateTime
  PostCopy: string
  Platform: string
}

// Base TouchPoint Create DTO
export interface TouchPointCreateDto {
  CampaignId: string
  WebId?: string // Optional: Campaign-level touchpoints may not be tied to a specific web
  Name: string
  Type: string // TouchPointType
  EmailProperties?: EmailTouchPointPropertiesDto
  MeetingProperties?: MeetingTouchPointPropertiesDto
  PhoneProperties?: PhoneTouchPointPropertiesDto
  SocialProperties?: SocialTouchPointPropertiesDto
}

// TouchPoint View DTO
export interface TouchPointViewDto {
  Id: string
  WorkspaceId: string
  CampaignId: string
  WebId?: string // Optional: Campaign-level touchpoints may not be tied to a specific web
  Name: string
  Type: string // TouchPointType
  ETag: string
  EmailProperties?: EmailTouchPointPropertiesDto
  MeetingProperties?: MeetingTouchPointPropertiesDto
  PhoneProperties?: PhoneTouchPointPropertiesDto
  SocialProperties?: SocialTouchPointPropertiesDto
}

// TouchPoint Update DTO
export interface TouchPointUpdateDto {
  Name: string
  ETag: string
  EmailProperties?: EmailTouchPointPropertiesDto
  MeetingProperties?: MeetingTouchPointPropertiesDto
  PhoneProperties?: PhoneTouchPointPropertiesDto
  SocialProperties?: SocialTouchPointPropertiesDto
}

// Lookup DTOs - now using standardized format
export type { LookupOptionDto } from '@/types/lookups'

export const TOUCHPOINT_TYPE_LABELS: Record<string, string> = {
  Email: 'Email',
  Phone: 'Call',
  Social: 'Social Post',
  Meeting: 'Meeting',
}

export const TOUCHPOINT_TYPE_ICONS: Record<string, string> = {
  Email: '📧',
  Phone: '📞',
  Social: '📱',
  Meeting: '🤝',
}
